// UI de los bloques B-O de 016 (Fase 5, Tanda 2) — modelo y fuente en
// `bloques016Datos.ts`. El bloque A (establecimiento/usuario) lo resuelve
// `EncabezadoMspCampos` en `Formulario016.tsx`, igual que 113/114/115.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { Bloque, CamposSimples, SelectSimple } from './camposSimples';
import { MARCAS_PRE_DEF } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { EscalaClinica } from './uci/EscalaClinica';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { ProfesionalPorTurno } from './ProfesionalPorTurno';
import { CAMPOS_PROFESIONAL_PAPEL } from './profesionalPorTurnoDatos';
import {
  CAMPOS_ANTROPOMETRIA_016,
  CAMPOS_FAMILIARES_016,
  CAMPOS_NATALES_016,
  CAMPOS_OBSTETRICOS_MADRE_016,
  CAMPOS_SATURACION_016,
  COLUMNAS_CONSTANTES_016,
  DEFINICION_APGAR_016,
  FILAS_CONSTANTES_016,
  TURNOS_PROFESIONAL_016,
  GRUPOS_SI_NO_016,
  MINUTOS_APGAR_016,
  NUM_HERMANOS_016,
  OPCIONES_CONDICION_PARTO_HERMANO_016,
  OPCIONES_ESTADO_SISTEMA_016,
  OPCIONES_TIPO_PARTO_HERMANO_016,
  SISTEMAS_EXAMEN_016,
  porcentajePerdidaPeso016,
  type EstadoBitacora016,
  type ValorSiNo,
} from './bloques016Datos';

export interface Bloques016Props {
  readonly estado: EstadoBitacora016;
  readonly onChange: (estado: EstadoBitacora016) => void;
}

export function Bloques016({ estado, onChange }: Bloques016Props): JSX.Element {
  const { t } = useTranslation();
  const opcionesSiNo = [
    { valor: 'si', etiqueta: t('formularios.f016.si') },
    { valor: 'no', etiqueta: t('formularios.f016.no') },
  ];
  const perdidaPeso = porcentajePerdidaPeso016(estado.antropometria);

  function grupoSiNo(clave: string): JSX.Element {
    const grupo = GRUPOS_SI_NO_016.find((g) => g.clave === clave)!;
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {grupo.items.map((i) => (
          <SelectSimple
            key={i.clave}
            id={`f016-sino-${i.clave}`}
            etiqueta={i.etiqueta}
            valor={estado.siNo[i.clave] ?? ''}
            opciones={opcionesSiNo}
            onChange={(v) => onChange({ ...estado, siNo: { ...estado.siNo, [i.clave]: v as ValorSiNo } })}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Bloque titulo={t('formularios.f016.familiaresTitulo')}>
        <div className="flex flex-col gap-3">
          {CAMPOS_FAMILIARES_016.map((c) => (
            <AreaDictado
              key={c.clave}
              id={`f016-fam-${c.clave}`}
              etiqueta={c.etiqueta}
              valor={estado.familiares[c.clave] ?? ''}
              onChange={(v) => onChange({ ...estado, familiares: { ...estado.familiares, [c.clave]: v } })}
              filas={2}
            />
          ))}
        </div>

        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f016.hermanosTitulo')}</h5>
        <div className="flex flex-col gap-3">
          {Array.from({ length: NUM_HERMANOS_016 }, (_, i) => {
            const n = String(i + 1);
            const fila = estado.hermanos[n];
            const actualizar = (parcial: Partial<typeof fila>): void => onChange({ ...estado, hermanos: { ...estado.hermanos, [n]: { ...fila, ...parcial } } });
            const opcionesTipo = OPCIONES_TIPO_PARTO_HERMANO_016.map((o) => ({ valor: o, etiqueta: o }));
            const opcionesCondicion = OPCIONES_CONDICION_PARTO_HERMANO_016.map((o) => ({ valor: o, etiqueta: o }));
            return (
              <div key={n} className="grid grid-cols-1 gap-2 rounded-md border border-border p-2 sm:grid-cols-4">
                <SelectSimple id={`f016-her-${n}-tipo`} etiqueta={`${t('formularios.f016.hermano')} ${n} — ${t('formularios.f016.tipoParto')}`} valor={fila.tipoParto} opciones={opcionesTipo} onChange={(v) => actualizar({ tipoParto: v })} />
                <SelectSimple id={`f016-her-${n}-cond`} etiqueta={t('formularios.f016.condicionParto')} valor={fila.condicionParto} opciones={opcionesCondicion} onChange={(v) => actualizar({ condicionParto: v })} />
                <CampoDictado id={`f016-her-${n}-edad`} etiqueta={t('formularios.f016.edadActual')} valor={fila.edadActual} onChange={(v) => actualizar({ edadActual: v })} />
                <CampoDictado id={`f016-her-${n}-obs`} etiqueta={t('formularios.f016.observaciones')} valor={fila.observaciones} onChange={(v) => actualizar({ observaciones: v })} />
              </div>
            );
          })}
        </div>

        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f016.obstetricosMadreTitulo')}</h5>
        <CamposSimples prefijoId="f016-obs" campos={CAMPOS_OBSTETRICOS_MADRE_016} valores={estado.obstetricosMadre} onChange={(v) => onChange({ ...estado, obstetricosMadre: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f016.prenatalesTitulo')}>
        <AreaDictado id="f016-antecedentes-prenatales" etiqueta="" valor={estado.antecedentesPrenatales} onChange={(v) => onChange({ ...estado, antecedentesPrenatales: v })} filas={3} />
      </Bloque>

      <Bloque titulo={t('formularios.f016.natalesTitulo')}>
        <CamposSimples prefijoId="f016-nat" campos={CAMPOS_NATALES_016} valores={estado.natales} onChange={(v) => onChange({ ...estado, natales: v })} />
        {grupoSiNo('natales')}
      </Bloque>

      <Bloque titulo={t('formularios.f016.apgarTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f016.apgarNota')}</p>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {MINUTOS_APGAR_016.map((minuto) => (
            <div key={minuto} className="flex flex-col gap-1">
              <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f016.apgarMinuto', { minuto })}</h5>
              <EscalaClinica idPrefix={`f016-apgar-${minuto}`} definicion={DEFINICION_APGAR_016} respuestas={estado.apgar[minuto]} onChange={(r) => onChange({ ...estado, apgar: { ...estado.apgar, [minuto]: r } })} />
            </div>
          ))}
        </div>
      </Bloque>

      <Bloque titulo={t('formularios.f016.intervencionesTitulo')}>{grupoSiNo('intervenciones')}</Bloque>
      <Bloque titulo={t('formularios.f016.profilaxisTitulo')}>{grupoSiNo('profilaxis')}</Bloque>
      <Bloque titulo={t('formularios.f016.vacunasTitulo')}>{grupoSiNo('vacunas')}</Bloque>

      <Bloque titulo={t('formularios.f016.constantesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f016.constantesNota')}</p>
        <TablaConColumnas filas={FILAS_CONSTANTES_016} columnas={COLUMNAS_CONSTANTES_016} estado={estado.constantes} onChange={(c) => onChange({ ...estado, constantes: c })} />
      </Bloque>

      <Bloque titulo={t('formularios.f016.examenFisicoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f016.examenFisicoNota')}</p>
        <CamposSimples prefijoId="f016-antro" campos={CAMPOS_ANTROPOMETRIA_016} valores={estado.antropometria} onChange={(v) => onChange({ ...estado, antropometria: v })} />
        {perdidaPeso !== undefined && <p className="text-sm font-medium text-hce-ink">{t('formularios.f016.porcentajePerdidaPesoValor', { valor: perdidaPeso.toFixed(1) })}</p>}
        <div className="flex flex-col gap-2">
          {SISTEMAS_EXAMEN_016.map((s) => {
            const fila = estado.sistemas[s.clave];
            const actualizar = (parcial: Partial<typeof fila>): void => onChange({ ...estado, sistemas: { ...estado.sistemas, [s.clave]: { ...fila, ...parcial } } });
            return (
              <div key={s.clave} className="grid grid-cols-1 gap-2 rounded-md border border-border p-2 lg:grid-cols-2">
                <div className="grid grid-cols-[9rem_1fr] items-end gap-2">
                  <SelectSimple id={`f016-sis-${s.clave}-ini`} etiqueta={`${s.etiqueta} — ${t('formularios.f016.inicial')}`} valor={fila.inicialEstado} opciones={OPCIONES_ESTADO_SISTEMA_016} onChange={(v) => actualizar({ inicialEstado: v })} />
                  <CampoDictado id={`f016-sis-${s.clave}-ini-desc`} etiqueta={`${s.etiqueta} ${t('formularios.f016.inicial')} ${t('formularios.f016.descripcion')}`} valor={fila.inicialDescripcion} onChange={(v) => actualizar({ inicialDescripcion: v })} />
                </div>
                <div className="grid grid-cols-[9rem_1fr] items-end gap-2">
                  <SelectSimple id={`f016-sis-${s.clave}-egr`} etiqueta={`${s.etiqueta} — ${t('formularios.f016.alEgreso')}`} valor={fila.egresoEstado} opciones={OPCIONES_ESTADO_SISTEMA_016} onChange={(v) => actualizar({ egresoEstado: v })} />
                  <CampoDictado id={`f016-sis-${s.clave}-egr-desc`} etiqueta={`${s.etiqueta} ${t('formularios.f016.alEgreso')} ${t('formularios.f016.descripcion')}`} valor={fila.egresoDescripcion} onChange={(v) => actualizar({ egresoDescripcion: v })} />
                </div>
              </div>
            );
          })}
        </div>
        <CamposSimples prefijoId="f016-sat" campos={CAMPOS_SATURACION_016} valores={estado.saturacion} onChange={(v) => onChange({ ...estado, saturacion: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f016.diagnosticoInicialTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f016.diagnosticoNota')}</p>
        {estado.diagnosticosInicial.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f016.diagnosticoInicialTitulo')} ${i + 1}`}
            marcaLabel={t('formularios.f016.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => onChange({ ...estado, diagnosticosInicial: estado.diagnosticosInicial.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f016.planInicialTitulo')}>
        <AreaDictado id="f016-plan-inicial" etiqueta="" valor={estado.planInicial} onChange={(v) => onChange({ ...estado, planInicial: v })} filas={4} />
      </Bloque>

      <Bloque titulo={t('formularios.f016.diagnosticoEgresoTitulo')}>
        <CampoDictado id="f016-clasif-neonato" etiqueta={t('formularios.f016.clasificacionNeonato')} valor={estado.clasificacionNeonato} onChange={(v) => onChange({ ...estado, clasificacionNeonato: v })} />
        {estado.diagnosticosEgreso.map((d, i) => (
          <SelectorDiagnosticoCie10
            key={i}
            label={`${t('formularios.f016.diagnosticoEgresoTitulo')} ${i + 1}`}
            value={d}
            onChange={(v) => onChange({ ...estado, diagnosticosEgreso: estado.diagnosticosEgreso.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f016.planEgresoTitulo')}>
        <AreaDictado id="f016-plan-egreso" etiqueta="" valor={estado.planEgreso} onChange={(v) => onChange({ ...estado, planEgreso: v })} filas={4} />
      </Bloque>

      <ProfesionalPorTurno
        prefijoI18n="formularios.f016.profesionalTurnos"
        prefijoId="f016-prof"
        campos={CAMPOS_PROFESIONAL_PAPEL}
        turnos={TURNOS_PROFESIONAL_016}
        estado={estado.profesional}
        onChange={(p) => onChange({ ...estado, profesional: p })}
      />
    </div>
  );
}
